import { ArrowRightOutlined } from "@ant-design/icons";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useRegisterMutation } from "../api/api";
import { IStatusMsg } from "../types/types";
import { isRecaptchaEnabled } from "../utils/app";
import { EXHUT_REDIRECT } from "../utils/constants";
import { ExternalLink } from "lucide-react";
import { Button } from "antd";

export default function RegisterPage3() {
  const { mutateAsync: registerAsync, isLoading: isRegisterLoading } =
    useRegisterMutation();

  const [statusMsg, setStatusMsg] = useState<IStatusMsg | undefined>();
  const [registerSuccess, setRegisterSuccess] = useState<boolean>(false);
  const [recaptchaVerified, setRecaptchaVerified] = useState(
    !isRecaptchaEnabled
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const isLogout = new URLSearchParams(searchParams).get("logout");

  useEffect(() => {
    if (isLogout) {
      searchParams.delete("logout");
      setSearchParams(searchParams);

      setStatusMsg({ type: "error", text: "Please sign in." });
    }
  }, [isLogout]);

  const formik = useFormik({
    initialValues: {
      password: "",
      email: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Please enter password."),
      email: Yup.string()
        .matches(
          /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$/,
          "Please enter valid email address"
        )
        .required("Please enter email."),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      localStorage.setItem(EXHUT_REDIRECT, "/login");
      registerAsync({
        email: values.email.toLowerCase(),
        password: values.password,
      })
        .then(() => {
          setRegisterSuccess(true);
          setStatusMsg({
            type: "success",
            text: "We have emailed you a verification link to your email. Please use it to proceed",
          });
        })
        .catch((err) => {
          setStatusMsg({
            type: "error",
            text: err.response.data.message ?? "Whoops! Something went wrong.",
          });
        });
    },
  });

  useEffect(() => {
    if (!!statusMsg) {
      setStatusMsg(undefined);
    }
  }, [formik.values]);

  const setLoginReady = (e: any) => {
    // console.log("Ready recaptch", )
    setRecaptchaVerified(true);
  };

  return (
    <div className="min-h-screen mx-auto max-w-5xl px-6 py-12">
      <div className="flex justify-center border border-gray-300 text-gray-700 hover:bg-gray-50 mb-4 gap-2 p-2 rounded-lg">
        Already have an account?
        <Link to={"/login"} className="text-blue-500">
          Login
        </Link>
      </div>

      <div className="border border-gray-300 p-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Account
          </h1>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 ">
          {/* Business Owner Card */}
          <a
            id="admin-role"
            href="https://expensehut.com"
            target="_blank"
            rel="noreferrer"
            className="bg-card flex rounded-2xl px-8 py-12 bg-gradient-to-r from-black to-purple-900"
          >
            <div className="relative z-10 ">
              <h2 className="text-2xl font-bold text-white mb-3">
                Point-Of-Sale
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Create and manage your business, set up stores, and grow your
                operations with our platform.
              </p>
              <div className="flex bg-gray-300 rounded-md p-2 text-black items-center gap-2 justify-center">
                Sign up
                <ExternalLink className="w-5 h-5" />
              </div>
            </div>
          </a>

          {/* Employee Card */}
          <a
            id="admin-role"
            href="https://app.expensehut.com"
            target="_blank"
            rel="noreferrer"
            className="bg-card flex rounded-2xl px-8 py-12 bg-gradient-to-r from-black to-blue-900"
          >
            <div className="relative">
              <h2 className="text-2xl font-bold text-white mb-1">
                Expense Tracker
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Join an existing business and start managing your tasks and
                operations.
              </p>
              <div className="flex bg-gray-300 rounded-md p-2 text-black items-center gap-2 justify-center">
                Sign up
                <ExternalLink className="w-5 h-5" />
              </div>
            </div>

            {/* Gradient overlay on hover */}
          </a>
        </div>
      </div>
    </div>
  );
}
